import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { HiChevronLeft, HiCalendar } from 'react-icons/hi';
import { HiClock, HiMapPin, HiArrowsRightLeft } from 'react-icons/hi2';
import { FaThumbsUp, FaThumbsDown, FaQuestion, FaClock } from 'react-icons/fa';
import { COLOR_SUCCESS, COLOR_ALERT, COLOR_ERROR, COLOR_PRIMARY } from '../../constants';
import { PersonalEvent as Event, EventParticipationAnswer } from '../../types/event';
import { CustomFieldResponses } from '../../types/invites';
import { eventService } from '../../services';
import { getUserId } from '../../services/httpService';
import { getEventAttendees, getPollResponses, getEventAttendanceAnswers } from '../../utils/eventUtils';
import { formatCustomFieldResponsesFromCustomFields } from '../../utils/formatUtils';
import { downloadEventAsICS } from '../../utils/calendarUtils';
import Button from '../misc/Button';
import AttendanceComponent from '../misc/AttendanceComponent';
import GenericInput from '../forms/GenericInput';
import CustomFieldsForm from '../forms/CustomFieldsForm';
import WarningModal from './WarningModal';
import HorizontalSponsorsList from '../misc/HorizontalSponsorsList';

interface EventDetailModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  eventId: number | null;
}

export default function EventDetailModal({ open, setOpen, eventId }: EventDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<EventParticipationAnswer | null>(null);
  const [remark, setRemark] = useState<string>('');
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>([]);
  const [initialAnswer, setInitialAnswer] = useState<EventParticipationAnswer | null>(null);
  const [initialRemark, setInitialRemark] = useState<string>('');
  const [initialCustomFieldResponses, setInitialCustomFieldResponses] = useState<CustomFieldResponses>([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'participants'>('details');
  const isDetailsTab = activeTab === 'details';
  const isParticipantsTab = activeTab === 'participants';
  const attendanceAnswers = getEventAttendanceAnswers(i18n);
  const attendanceLabelMap = Object.fromEntries(attendanceAnswers.map(([key, label]) => [key, label])) as Record<
    string,
    string
  >;

  // Use the same icons and colors as the AttendanceComponent
  const attendanceIconMap: Record<string, any> = {
    [EventParticipationAnswer.YES]: FaThumbsUp,
    [EventParticipationAnswer.MAYBE]: FaQuestion,
    [EventParticipationAnswer.NO]: FaThumbsDown,
    [EventParticipationAnswer.PENDING]: FaClock,
  };

  const attendanceColorMap: Record<string, string> = {
    [EventParticipationAnswer.YES]: COLOR_SUCCESS,
    [EventParticipationAnswer.MAYBE]: COLOR_ALERT,
    [EventParticipationAnswer.NO]: COLOR_ERROR,
    [EventParticipationAnswer.PENDING]: COLOR_PRIMARY,
  };

  const participantOrder: Record<string, number> = {
    [EventParticipationAnswer.YES]: 0,
    [EventParticipationAnswer.MAYBE]: 1,
    [EventParticipationAnswer.NO]: 2,
    [EventParticipationAnswer.PENDING]: 3,
  };

  const renderAttendanceIcon = (answer?: EventParticipationAnswer) => {
    const Icon = attendanceIconMap[answer ?? EventParticipationAnswer.PENDING];
    const bg = attendanceColorMap[answer ?? EventParticipationAnswer.PENDING];
    const label = attendanceLabelMap[answer ?? 'PENDING'] ?? answer ?? '';

    return (
      <div
        className="flex h-6 w-6 items-center justify-center rounded-full"
        style={{ backgroundColor: bg }}
        title={label}
        aria-label={label}>
        {Icon ? (
          // eslint-disable-next-line react/jsx-pascal-case
          <Icon className="h-3 w-3 text-secondary-50" />
        ) : (
          <span className="text-xs text-details/70">{label}</span>
        )}
      </div>
    );
  };

  const hasUnsavedChanges = () => {
    if (selectedAnswer !== initialAnswer) return true;
    if (remark !== initialRemark) return true;
    if (JSON.stringify(customFieldResponses) !== JSON.stringify(initialCustomFieldResponses)) return true;
    return false;
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setShowWarningModal(true);
      return;
    }
    setOpen(false);
  };

  const handleConfirmClose = () => {
    setShowWarningModal(false);
    setOpen(false);
  };

  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [loadingTicketCode, setLoadingTicketCode] = useState(false);

  useEffect(() => {
    if (!open || !eventId) {
      setEvent(null);
      setSelectedAnswer(null);
      setRemark('');
      setCustomFieldResponses([]);
      setInitialAnswer(null);
      setInitialRemark('');
      setInitialCustomFieldResponses([]);
      setQrDataUrl('');
      return;
    }

    setLoading(true);
    eventService
      .getEvent(eventId)
      .then(setEvent)
      .catch((error) => {
        console.error('Failed to load event details:', error);
        toast.error(t('page.dashboard.event.loading'));
      })
      .finally(() => setLoading(false));
  }, [open, eventId, t]);

  useEffect(() => {
    (async () => {
      try {
        if (!event || !event.eventTicketUnit) return;
        setLoadingTicketCode(true);
        const res = await eventService.getMyTicketCode(event.id);
        if (res?.personalCode) {
          try {
            const dataUrl = await QRCode.toDataURL(res.personalCode);
            setQrDataUrl(dataUrl);
          } catch (err) {
            console.error('Failed to generate QR code:', err);
          }
        }
      } catch (err) {
        console.error('Failed to fetch ticket code:', err);
      } finally {
        setLoadingTicketCode(false);
      }
    })();
  }, [event]);

  useEffect(() => {
    try {
      const userId = getUserId();
      if (!userId || !event) return;

      const userAnswer = event.participants.find((participant) => participant.id === userId);
      if (userAnswer) {
        const answer = userAnswer.answer || EventParticipationAnswer.PENDING;
        const userRemark = userAnswer.description || '';
        setSelectedAnswer(answer);
        setRemark(userRemark);
        setInitialAnswer(answer);
        setInitialRemark(userRemark);
      }
      const responses = getPollResponses(userId, event.customFields);
      const formattedResponses = formatCustomFieldResponsesFromCustomFields(event.customFields, responses);
      setCustomFieldResponses(formattedResponses);
      setInitialCustomFieldResponses(formattedResponses);
    } catch (error) {
      console.error(error);
    }
  }, [event]);

  const validate = () => {
    if (!selectedAnswer || selectedAnswer === EventParticipationAnswer.PENDING) {
      toast.error(t('toast.error.field.noAnswer'));
      return false;
    }

    const numberOfAttendees = event ? getEventAttendees(event) : 0;
    const maximumAttendeesReached = event?.maximumAttendees ? numberOfAttendees >= event.maximumAttendees : false;

    if (maximumAttendeesReached && selectedAnswer === EventParticipationAnswer.YES) {
      toast.error(t('toast.error.events.maximumAttendeesReached'));
      return false;
    }

    if (event && event.customFields.length > 0) {
      return event.customFields.every((field) => {
        if (field.mandatory && !customFieldResponses[field.customFieldId]) {
          toast.error(t('toast.error.field.mandatoryStar'));
          return false;
        }
        return true;
      });
    }

    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validate() || !event?.id) return;

    try {
      await eventService.respondToEvent(`${event.id}`, selectedAnswer!, remark);

      if (event.customFields.length > 0) {
        await eventService.respondToEventPollBulk(`${event.id}`, customFieldResponses);
      }

      toast.success(t('page.dashboard.event.rsvpUpdated'));
      // Reload event data
      const updatedEvent = await eventService.getEvent(event.id);
      setEvent(updatedEvent);
    } catch (error) {
      console.error('Failed to update RSVP:', error);
      toast.error(t('page.dashboard.event.rsvpFailed'));
    }
  };

  const formatDateTime = (date: Date | string) => {
    const dateObj = new Date(date);
    return {
      day: dateObj.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' }),
      time: dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
    };
  };

  const getGoogleMapsUrl = (location: string) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;

  const numberOfAttendees = event ? getEventAttendees(event) : 0;
  const deadlineIsPassed = event?.deadline ? new Date() > new Date(event.deadline) : false;
  const maximumAttendeesReached = event?.maximumAttendees ? numberOfAttendees >= event.maximumAttendees : false;
  const remarkPlaceholder = selectedAnswer
    ? `${t(`page.events.invite.placeholder.eg`)} ${t(`page.events.invite.placeholder.${selectedAnswer}.reason${Math.floor(Math.random() * 10) + 1}`)}`
    : '';

  return (
    <>
      <WarningModal
        open={showWarningModal}
        setOpen={setShowWarningModal}
        title={t('page.events.detail.unsavedChangesTitle')}
        description={t('page.events.detail.unsavedChangesWarning')}
        buttonText={t('general.close')}
        onSubmit={handleConfirmClose}
      />
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
          </Transition.Child>

          {/* Sliding Panel */}
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full px-8 py-8">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full">
                  <Dialog.Panel className="pointer-events-auto h-full w-screen max-w-md">
                    <div className="flex h-full flex-col rounded-lg bg-secondary-50 p-4">
                      {loading ? (
                        <div className="flex h-full items-center justify-center">
                          <p className="text-details">{t('general.loading')}</p>
                        </div>
                      ) : event ? (
                        <>
                          {/* Header with back and menu buttons */}
                          <div className="flex items-center justify-between bg-secondary-50 px-4 py-3">
                            <button
                              type="button"
                              className="rounded-md p-1 transition-colors hover:bg-secondary-200"
                              onClick={handleClose}>
                              <HiChevronLeft className="h-6 w-6 text-primary" />
                            </button>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="rounded-md p-1 transition-colors hover:bg-secondary-200"
                                onClick={() => downloadEventAsICS(event)}
                                title={t('page.events.detail.addToCalendar')}>
                                <HiCalendar className="h-5 w-5 text-primary" />
                              </button>
                            </div>
                          </div>

                          <div className="flex flex-1 flex-col overflow-hidden">
                            <div className="px-4 pt-4">
                              <div className="mb-4 flex justify-center">
                                {event.picture ? (
                                  <div className="mb-4">
                                    <img src={event.picture} alt={event.title} className="h-40 w-40 rounded-lg" />
                                  </div>
                                ) : (
                                  <div className="mb-4 flex h-40 w-40 items-center justify-center rounded-lg bg-primary">
                                    <HiCalendar className="h-16 w-16 text-secondary" />
                                  </div>
                                )}
                              </div>

                              <h2 className="mb-4 text-center text-2xl font-bold text-primary">{event.title}</h2>

                              <nav className="flex justify-center gap-6">
                                <button
                                  type="button"
                                  onClick={() => setActiveTab('details')}
                                  className={classNames('border-b-2 px-4 py-2 text-sm font-medium transition-colors', {
                                    'border-primary text-primary': isDetailsTab,
                                    'border-transparent text-gray-500 hover:text-gray-700': !isDetailsTab,
                                  })}>
                                  {t('page.events.detail.tabs.details')}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => setActiveTab('participants')}
                                  className={classNames('border-b-2 px-4 py-2 text-sm font-medium transition-colors', {
                                    'border-primary text-primary': isParticipantsTab,
                                    'border-transparent text-gray-500 hover:text-gray-700': !isParticipantsTab,
                                  })}>
                                  {t('page.events.detail.tabs.participants')}
                                </button>
                              </nav>
                            </div>

                            {/* Scrollable content that switches based on active tab */}
                            <div className="flex-1 overflow-y-auto px-4 py-4">
                              {isDetailsTab ? (
                                <>
                                  {eventId != null && (
                                    <div className="mb-6">
                                      <HorizontalSponsorsList eventId={eventId} />
                                    </div>
                                  )}
                                  {/* Description */}
                                  {event.description && (
                                    <div className="mb-6">
                                      <h3 className="mb-2 text-sm font-semibold text-primary">
                                        {t('page.events.detail.description')}
                                      </h3>
                                      <p className="text-sm leading-relaxed text-details/80">
                                        {event.description}
                                        {event.description.length > 150 && (
                                          <button type="button" className="ml-1 text-sm text-primary underline">
                                            {t('page.events.detail.seeMore')}
                                          </button>
                                        )}
                                      </p>
                                    </div>
                                  )}

                                  {/* Event Details */}
                                  <div className="mb-6 space-y-4">
                                    {event.maximumAttendees && event.participants && (
                                      <div className="flex items-start gap-3">
                                        <div className="flex items-center justify-center rounded-lg bg-primary p-3">
                                          <HiArrowsRightLeft className="h-5 w-5 text-secondary" />
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-primary">
                                            {t('page.events.detail.spotsLeft')}
                                          </p>
                                          <p className="text-xs text-details/70">
                                            {t('page.events.detail.alreadyGoing', {
                                              count: numberOfAttendees,
                                              max: event.maximumAttendees,
                                            })}
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Date and Time */}
                                    <div className="flex items-start gap-3">
                                      <div className="flex items-center justify-center rounded-lg bg-primary p-3">
                                        <HiClock className="h-5 w-5 text-secondary" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-primary">
                                          {formatDateTime(event.startTime).day}
                                        </p>
                                        <p className="text-xs text-details/70">
                                          {formatDateTime(event.startTime).time}
                                          {event.endTime && ` - ${formatDateTime(event.endTime).time}`}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Location */}
                                    {event.location && (
                                      <a
                                        href={getGoogleMapsUrl(event.location)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-start gap-3 rounded-lg hover:bg-secondary-200"
                                        aria-label={t('page.events.detail.openInMaps', { location: event.location })}>
                                        <div className="flex items-center justify-center rounded-lg bg-primary p-3">
                                          <HiMapPin className="h-5 w-5 text-secondary" />
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-primary">
                                            {t('page.events.detail.location')}
                                          </p>
                                          <p className="text-xs text-details/70">{event.location}</p>
                                        </div>
                                      </a>
                                    )}
                                  </div>

                                  {!deadlineIsPassed && !event.priceOfTicket && (
                                    <div className="mb-6 flex flex-col gap-4">
                                      <h2 className="mt-4 text-2xl font-semibold text-primary-900">
                                        {t('page.events.invite.areYouAttending')}
                                      </h2>
                                      <div className="flex gap-4">
                                        <AttendanceComponent
                                          selectedAnswer={selectedAnswer}
                                          setSelectedAnswer={setSelectedAnswer}
                                          disableYes={maximumAttendeesReached}
                                        />
                                      </div>
                                      <div>
                                        <GenericInput
                                          label={t('page.events.invite.note')}
                                          value={remark}
                                          onChange={(e) => setRemark(e.target.value)}
                                          placeholder={
                                            selectedAnswer !== EventParticipationAnswer.PENDING ? remarkPlaceholder : ''
                                          }
                                          textarea
                                        />
                                      </div>

                                      {event && event.customFields.length > 0 && (
                                        <>
                                          <div className="text-lg">{t('page.events.invite.polls.title')}</div>
                                          <CustomFieldsForm
                                            fields={event.customFields}
                                            response={customFieldResponses}
                                            setResponse={setCustomFieldResponses}
                                          />
                                        </>
                                      )}

                                      <div className="flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
                                        <Button
                                          variant="primary"
                                          className="rounded-lg px-[32px]"
                                          onClick={handleSubmit}>
                                          {t('general.save')}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div>
                                  <h3 className="mb-3 text-sm font-semibold text-primary">
                                    {t('page.events.detail.participants')}
                                  </h3>
                                  {event.participants.length === 0 ? (
                                    <p className="text-sm text-details/70">{t('page.events.detail.noParticipants')}</p>
                                  ) : (
                                    [...event.participants]
                                      .sort(
                                        (a, b) =>
                                          (participantOrder[a.answer ?? EventParticipationAnswer.PENDING] ?? 99) -
                                          (participantOrder[b.answer ?? EventParticipationAnswer.PENDING] ?? 99),
                                      )
                                      .map((p) => (
                                        <div key={p.id} className="flex items-center gap-3 rounded-lg p-2">
                                          {p.picture ? (
                                            <img
                                              src={p.picture}
                                              alt={`${p.firstName} ${p.lastName}`}
                                              className="h-10 w-10 rounded-full object-cover"
                                            />
                                          ) : (
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-medium text-white">
                                              {p.firstName?.charAt(0)}
                                              {p.lastName?.charAt(0)}
                                            </div>
                                          )}
                                          <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                              <div className="font-medium text-primary">
                                                {p.firstName} {p.lastName}
                                              </div>
                                              <div className="flex items-center justify-end text-sm text-details/70">
                                                {renderAttendanceIcon(p.answer)}
                                              </div>
                                            </div>
                                            {p.description && (
                                              <div className="text-xs text-details/70">{p.description}</div>
                                            )}
                                          </div>
                                        </div>
                                      ))
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Show ticket QR when the user has a ticket */}
                            {event?.eventTicketUnit && (
                              <div className="mb-4 flex flex-col items-center">
                                <h2 className="mb-2 text-lg font-semibold text-primary">{t('page.paid.yourTicket')}</h2>
                                {loadingTicketCode ? (
                                  <div className="py-4">
                                    <p className="text-details">{t('general.loading')}</p>
                                  </div>
                                ) : qrDataUrl ? (
                                  <div className="mb-4 flex flex-col items-center">
                                    <div className="mb-6 rounded-2xl bg-secondary-200 p-6">
                                      <img src={qrDataUrl} alt={t('page.paid.title')} className="w-56" />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="py-4">
                                    <p className="text-sm text-primary">{t('page.paid.title')}</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Close button always visible */}
                            <div className="px-4 py-4">
                              <Button
                                variant="primary"
                                onClick={handleClose}
                                className="w-full justify-center rounded-lg">
                                {t('general.close')}
                              </Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <p className="text-details">{t('page.dashboard.event.notFound')}</p>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
